import { gql, useQuery } from '@apollo/client';

import { useFeatureFlags } from 'hooks/useFeatureFlags/useFeatureFlags';
import { usePatient } from 'hooks/usePatient/usePatient';
import { utils } from 'kb-shared';
import { getLabInfo, LabInfo } from 'kb-shared/utilities/kindbody_contacts';
import { isPortlandPatient } from 'kb-shared/utilities/lab.helper';
import { formatPhoneNumber } from 'kb-shared/utilities/phones';
import { memberServicesPhone } from 'screens/Book/memberServicesPhone';

type ResponseData = {
  journeyType: string;
};

const CONTACT_INFO = gql`
  query contactInfo {
    journeyType
  }
`;

export const useContactInfo = () => {
  const { patient, isLoggedIn } = usePatient();
  const { ppNetworkClinicBooking } = useFeatureFlags();

  const defaultLabInfo = getLabInfo();
  const marketId = patient?.lab?.id || '';
  const withNetworkPhone = (labInfo: LabInfo): LabInfo => {
    if (!ppNetworkClinicBooking) return labInfo;

    const phoneNumber = memberServicesPhone(patient?.patientMembership?.employerPhone);

    return {
      ...labInfo,
      number: phoneNumber,
      numberLabel: formatPhoneNumber(phoneNumber) || phoneNumber
    };
  };

  const { data, error } = useQuery<ResponseData>(CONTACT_INFO, {
    skip: !isLoggedIn
  });

  if (!isLoggedIn) return defaultLabInfo;

  const info = getLabInfo(marketId);

  const employer = patient?.patientMembership?.employer;
  const employerPhone = patient?.patientMembership?.employerPhone;

  if (employer) {
    // Flag off: Portland patients keep the market number.
    const phoneNumber = !isPortlandPatient(marketId)
      ? employerPhone || defaultLabInfo.number
      : info.number;

    return withNetworkPhone({
      ...info,
      email: utils.KBContacts.employeeBenefitsEmail,
      number: phoneNumber,
      numberLabel: formatPhoneNumber(phoneNumber) || phoneNumber
    });
  }

  if (isPortlandPatient(marketId)) {
    return withNetworkPhone(getLabInfo(marketId));
  }

  if (!data || error || !data.journeyType) {
    return withNetworkPhone({
      ...info,
      email: defaultLabInfo.email,
      number: defaultLabInfo.number,
      numberLabel: defaultLabInfo.numberLabel
    });
  }

  return withNetworkPhone({
    ...info
  });
};

export default useContactInfo;
